import {
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuRootProps,
	type MenuVariantProps,
	toast,
} from "@overmap-ai/blocks"
import { OrganizationAccessLevel, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, selectGeoImageById } from "@overmap-ai/core"
import { useImageViewer } from "@overmap-ai/forms"
import { useOpenState } from "@overmap-ai/hooks"
import { memo, type ReactElement, useCallback } from "react"

import { useConfirmDeleteDialog, useFetchFileFromFileModel } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { GeoImageDetailsDialog } from "../Dialogs/GeoImageDetailsDialog"
import { LocationGroup } from "../MenuGroups/LocationGroup"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"

interface GeoImageDropdownMenu
	extends Omit<MenuRootProps, "children">, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	children: ReactElement
	geoImageId: string
}

export const GeoImageDropdownMenu = memo((props: GeoImageDropdownMenu) => {
	const { children, geoImageId, disabled, size, variant, accentColor, radius, ...rest } = props

	const geoImage = useAppSelector(selectGeoImageById(geoImageId))

	if (!geoImage) {
		throw new Error(`GeoImage with id ${geoImageId} not found in store.`)
	}

	const openImageViewer = useImageViewer()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const [showDetailsDialog, openDetailsDialog, closeDetailsDialog] = useOpenState(false)

	const [file, error] = useFetchFileFromFileModel(geoImage, sdk.geoImages)

	const handleView = useCallback(() => {
		if (!file) return

		openImageViewer((close) => ({
			file,
			onClose: close,
		}))
	}, [file, openImageViewer])

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete geo image",
			description: "Are you sure you want to delete this geo image?",
			onAction: () => {
				sdk.geoImages.delete(geoImage.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete geo image",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
			},
		})
	}, [geoImage.uuid, openConfirmDeleteDialog])

	return (
		<>
			<Menu.Root {...rest}>
				<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
				<Menu.Content size={size} variant={variant} accentColor={accentColor} radius={radius}>
					<Menu.Item disabled={error} onSelect={handleView}>
						<LuIcon icon={modelIconMapping.expand} />
						View
					</Menu.Item>
					<Menu.Item onSelect={openDetailsDialog}>
						<LuIcon icon={modelIconMapping.edit} />
						Edit
					</Menu.Item>
					<Menu.Separator />
					<LocationGroup coordinates={geoImage.geo_marker?.coordinates} />
					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
						<Menu.Separator />
						<Menu.Item onSelect={handleDelete}>
							<LuIcon icon={modelIconMapping.delete} />
							Delete
						</Menu.Item>
					</OrganizationAccessRequired>
				</Menu.Content>
			</Menu.Root>
			<GeoImageDetailsDialog
				geoImageId={geoImage.uuid}
				open={showDetailsDialog}
				onOpenChange={closeDetailsDialog}
				size="sm"
			/>
		</>
	)
})
GeoImageDropdownMenu.displayName = "GeoImageDropdownMenu"
